import React from 'react';
import { User, Calendar } from 'lucide-react';
import { CASA_LUCHIAN_LOGO } from '../data/mockMaps';
import { getImageWithPreset } from '../utils/imageOptimization';

const MapCard = ({ map, onClick, viewMode = 'grid' }) => {
  // Use optimized image URLs based on view mode
  const displayImage = viewMode === 'list' 
    ? getImageWithPreset(map.thumbnailUrl || map.imageUrl, 'thumbnail')
    : getImageWithPreset(map.thumbnailUrl || map.imageUrl, 'card');

  if (viewMode === 'list') {
    return (
      <div
        onClick={() => onClick(map)}
        className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6 p-3 sm:p-4"
        data-testid="map-card"
      >
        {/* Image - Full width on mobile, fixed width on desktop */}
        <div className="w-full sm:w-32 md:w-48 flex-shrink-0 relative aspect-[4/3] sm:aspect-[3/4] overflow-hidden bg-gray-100 rounded-lg">
          <img
            src={displayImage}
            alt={map.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <img
            src={CASA_LUCHIAN_LOGO}
            alt="Casa Luchian"
            className="absolute bottom-2 right-2 w-8 h-8 opacity-80"
          />
          <div className="absolute top-2 right-2 bg-[#d4af37] text-white px-2 py-0.5 rounded-full text-xs font-semibold shadow-md">
            {map.year}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl md:text-2xl font-serif font-bold text-gray-900 mb-2 sm:mb-3 line-clamp-2">{map.title}</h3>
          {map.subtitle && (
            <p className="text-xs sm:text-sm text-gray-600 italic mb-2 sm:mb-3 line-clamp-1">{map.subtitle}</p>
          )}

          <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mb-2 sm:mb-3 text-xs sm:text-sm text-gray-600">
            <div className="flex items-center gap-1.5">
              <User size={14} className="text-[#d4af37] flex-shrink-0" />
              <span className="truncate">{map.author}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar size={14} className="text-[#d4af37] flex-shrink-0" />
              <span>{map.year}</span>
            </div>
          </div>

          <p className="text-xs sm:text-sm text-gray-700 leading-relaxed line-clamp-3 sm:line-clamp-4">{map.descriptionPreview || map.description}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={() => onClick(map)}
      className="bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer overflow-hidden hover:-translate-y-2 group"
      data-testid="map-card"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={displayImage}
          alt={map.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <img
          src={CASA_LUCHIAN_LOGO}
          alt="Casa Luchian"
          className="absolute bottom-2 right-2 w-10 h-10 md:w-12 md:h-12 opacity-80"
        />
        <div className="absolute top-3 right-3 bg-[#d4af37] text-white px-2.5 py-1 rounded-full text-xs font-semibold uppercase shadow-lg">
          {map.year}
        </div>
      </div>

      <div className="p-4 md:p-6">
        <h3 className="text-lg md:text-xl font-serif font-bold text-gray-900 mb-3 line-clamp-2 min-h-[3.5rem]">{map.title}</h3>

        <div className="flex flex-wrap gap-3 md:gap-4 mb-3 text-xs md:text-sm text-gray-600">
          <div className="flex items-center gap-1.5">
            <User size={14} className="text-[#d4af37] flex-shrink-0" />
            <span className="line-clamp-1">{map.author}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar size={14} className="text-[#d4af37] flex-shrink-0" />
            <span>{map.year}</span>
          </div>
        </div>

        <p className="text-xs md:text-sm text-gray-700 leading-relaxed line-clamp-3">{map.descriptionPreview || (map.description ? map.description.substring(0, 150) + '...' : '')}</p>
      </div>
    </div>
  );
};

export default MapCard;
